import { alpha, Box, type SxProps, type Theme, Typography, useTheme } from "@mui/material";
import { type FC, useEffect, useRef, useState } from "react";
import { FONT_SIZE, MOTION, NEUTRAL } from "@/theme/tokens";
import { prefersReducedMotion } from "@/utils/reducedMotion";
import { placeScaleLabels, type SystemLinearMeterScaleMark } from "./scaleMarkLayout";

export type { SystemLinearMeterScaleMark } from "./scaleMarkLayout";

/** Default translucency for the secondary/"projected" fill when no explicit `secondaryFill` is given. */
const SECONDARY_FILL_ALPHA = 0.42;
/** Target-tick geometry: a thin vertical rule straddling the track. */
const TARGET_TICK_WIDTH_PX = 2;
const TARGET_TICK_OVERHANG_PX = 3;
/** Scale-mark geometry: a hairline rule hanging below the track, plus the gap to its label row. */
const SCALE_TICK_WIDTH_PX = 1;
const SCALE_TICK_LENGTH_PX = 4;
const SCALE_LABEL_GAP_PX = 3;
/** Scale marks read as a quiet axis, so they default to a translucent tint of the tick color. */
const SCALE_TICK_ALPHA = 0.45;

/**
 * A thin, continuous "how far to the max" meter — a rounded track with a colored fill
 * from 0 → `value`. Unlike MUI `LinearProgress`, the fill color can be derived from the
 * live `value/max` ratio, so a laggard reads red and a maxed row reads green without the
 * caller wiring per-instance colors. Reuse for any inline points/target bar (a scoring
 * measure, an HPPD-to-goal indicator, a budget-used bar).
 *
 * Optional goal-meter extras (mirroring `SystemRadialGauge`, kept fully optional so existing
 * callers are unchanged):
 *  - `targetRatio` draws a bold target tick straddling the track at that fraction (e.g. a
 *    goal line the fill should reach). Colour it with `targetColor`.
 *  - `secondaryValue` draws a SECOND fill `0 → secondaryValue` *behind* the primary `value`
 *    fill, so the segment beyond `value` reads as a "projected / would-be" extension (a
 *    with-plan score, a forecast). Style it with `secondaryFill` (any CSS background — a
 *    solid, a gradient, or a hatch); defaults to a translucent tint of the primary fill.
 *  - `scaleMarks` turns the bar into a measurable axis: a hairline mark + optional label at
 *    each caller-chosen value, so the reader can judge WHERE the fill sits, not just how full.
 *    Marks are explicit rather than evenly spaced because an even axis lands its numbers at
 *    positions the bar doesn't draw anything at — the useful marks are the ones that coincide
 *    with the data (the scale ends, the value, the projection, the target).
 *
 * The fill animates from empty on mount via a CSS width transition (a predetermined
 * animation → CSS). Later value changes re-target the width directly so the same transition
 * glides current→new (interruptible) instead of snapping to zero and re-sweeping.
 * `prefers-reduced-motion` and `animate={false}` render the final width with no transition.
 * `value/max` is clamped to `[0,1]`; `max<=0` renders an empty track.
 */
export type SystemLinearMeterProps = {
	/** Current value the fill represents (clamped to `[0, max]`). */
	value: number;
	/** Scale maximum. Values `<= 0` render an empty track. */
	max: number;
	/** Fill color. Ignored when `colorResolver` is given. */
	color?: string;
	/** Derive the fill color from the current ratio (`value/max`, clamped `[0,1]`). Wins over `color`. */
	colorResolver?: (ratio: number) => string;
	/** Track (unfilled) color. Defaults to a theme neutral. */
	trackColor?: string;
	/** Optional target-tick position as a fraction of the track (`[0,1]`, clamped). Renders a bold vertical rule. */
	targetRatio?: number;
	/** Target-tick color. Defaults to the primary body-ink neutral (pass the brand gold for a goal line). */
	targetColor?: string;
	/**
	 * Optional secondary/"projected" value drawn as a fill `0 → secondaryValue` *behind* the
	 * primary `value` fill (clamped to `[0, max]`). The gap between `value` and `secondaryValue`
	 * reads as a projected extension. Omit for a single-fill meter.
	 */
	secondaryValue?: number;
	/** CSS `background` for the projected fill (solid / gradient / hatch). Defaults to a translucent tint of the primary fill. */
	secondaryFill?: string;
	/**
	 * Axis marks at explicit values — a hairline at each, plus its `label` beneath. Omit for a
	 * bare bar. ORDER THE ARRAY BY IMPORTANCE: when two labels would overlap the earlier one
	 * wins and the later one's label is dropped (its tick still draws), so a narrow bar sheds
	 * its least-important numbers instead of colliding.
	 */
	scaleMarks?: readonly SystemLinearMeterScaleMark[];
	/** Default scale-mark color for marks with no `color`. Defaults to a translucent tint of the label color. */
	scaleTickColor?: string;
	/** Default scale-label color for marks with no `color`. Defaults to the secondary body ink. */
	scaleLabelColor?: string;
	/** Bar height in px. Default 8. */
	height?: number;
	/** Corner radius in px. Default = half the height (fully rounded). */
	radius?: number;
	/** Animate the fill from empty on mount. Default true; forced off under reduced-motion. */
	animate?: boolean;
	/** Accessible summary (e.g. "40 of 100 points"). Sets `aria-valuenow/min/max` too. */
	ariaLabel?: string;
	/**
	 * Already-formatted reading of `value` for assistive tech (`aria-valuetext`, e.g. "$12,000"). Without
	 * it a screen reader announces the bare rounded number, which drops the unit a sighted reader sees.
	 */
	valueText?: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

export const SystemLinearMeter: FC<SystemLinearMeterProps> = ({
	value,
	max,
	color,
	colorResolver,
	trackColor,
	targetRatio,
	targetColor,
	secondaryValue,
	secondaryFill,
	scaleMarks,
	scaleTickColor,
	scaleLabelColor,
	height = 8,
	radius,
	animate = true,
	ariaLabel,
	valueText,
	dataTestId,
	sx,
}) => {
	const theme = useTheme();
	const ratio = max > 0 ? clamp(value / max, 0, 1) : 0;
	const hasSecondary = secondaryValue !== undefined && max > 0;
	const secondaryRatio = hasSecondary ? clamp((secondaryValue as number) / max, 0, 1) : 0;
	const resolvedTargetRatio = targetRatio !== undefined ? clamp(targetRatio, 0, 1) : null;

	// FIRST paint plays the empty→value entrance sweep; LATER value changes re-target the
	// width directly so the same CSS transition glides current→new (interruptible) instead of
	// snapping to zero and re-sweeping. The primary + projected fills share one animation gate.
	// Reduced-motion / animate=false jump straight to value.
	const [width, setWidth] = useState(0);
	const [secondaryWidth, setSecondaryWidth] = useState(0);
	const reduced = useRef(prefersReducedMotion());
	const hasMountedRef = useRef(false);
	useEffect(() => {
		const target = ratio * 100;
		const secondaryTarget = secondaryRatio * 100;
		const shouldAnimate = animate && !reduced.current;
		if (!shouldAnimate) {
			setWidth(target);
			setSecondaryWidth(secondaryTarget);
			hasMountedRef.current = true;
			return;
		}
		if (hasMountedRef.current) {
			setWidth(target);
			setSecondaryWidth(secondaryTarget);
			return;
		}
		setWidth(0);
		setSecondaryWidth(0);
		hasMountedRef.current = true;
		const raf = requestAnimationFrame(() => {
			setWidth(target);
			setSecondaryWidth(secondaryTarget);
		});
		return () => cancelAnimationFrame(raf);
	}, [ratio, secondaryRatio, animate]);

	const fillColor = colorResolver ? colorResolver(ratio) : (color ?? theme.palette.primary.main);
	const resolvedSecondaryFill = secondaryFill ?? alpha(fillColor, SECONDARY_FILL_ALPHA);
	const resolvedTargetColor = targetColor ?? NEUTRAL[900];
	const resolvedRadius = radius ?? height / 2;
	const animateFill = animate && !reduced.current;

	const fillTransition = animateFill ? `width ${MOTION.DURATION.INSTRUMENT_FILL}ms ${MOTION.EASE_OUT}` : undefined;

	const hasScale = scaleMarks !== undefined && scaleMarks.length > 0 && max > 0;
	const resolvedScaleLabelColor = scaleLabelColor ?? theme.palette.text.secondary;
	const resolvedScaleTickColor = scaleTickColor ?? alpha(resolvedScaleLabelColor, SCALE_TICK_ALPHA);
	// Label collision is resolved in px, so the track has to be measured. Placement re-runs on
	// resize: the same axis sheds more labels in a narrow container than in a wide one.
	const trackRef = useRef<HTMLDivElement | null>(null);
	const [trackWidth, setTrackWidth] = useState(0);
	useEffect(() => {
		const el = trackRef.current;
		if (!hasScale || !el) return;
		const observer = new ResizeObserver(([entry]) => setTrackWidth(entry.contentRect.width));
		observer.observe(el);
		return () => observer.disconnect();
	}, [hasScale]);
	const placedLabels = hasScale ? placeScaleLabels(scaleMarks, max, trackWidth) : [];
	// The target tick straddles the track edges and the scale marks hang below it, so the root
	// must not clip either.
	const overflow = resolvedTargetRatio !== null || hasScale ? "visible" : "hidden";

	const meter = (
		<Box
			ref={trackRef}
			data-testid={dataTestId}
			role="progressbar"
			aria-label={ariaLabel}
			aria-valuenow={Math.round(value)}
			aria-valuetext={valueText}
			aria-valuemin={0}
			aria-valuemax={Math.round(max)}
			sx={[
				{ position: "relative", height, borderRadius: `${resolvedRadius}px`, bgcolor: trackColor ?? NEUTRAL[200], overflow, width: "100%" },
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{/* Projected (secondary) fill — 0 → secondaryValue, drawn UNDER the primary fill. */}
			{hasSecondary && (
				<Box
					sx={{
						position: "absolute",
						left: 0,
						top: 0,
						height: "100%",
						width: `${secondaryWidth}%`,
						background: resolvedSecondaryFill,
						borderRadius: `${resolvedRadius}px`,
						transition: fillTransition,
					}}
				/>
			)}
			{/* Primary fill — 0 → value. Butts square into the projected fill when one is present. */}
			<Box
				sx={{
					position: "absolute",
					left: 0,
					top: 0,
					height: "100%",
					width: `${width}%`,
					// `background` (not `bgcolor`) so the fill can be a gradient/hatch, not only a solid color.
					background: fillColor,
					borderRadius: hasSecondary ? `${resolvedRadius}px 0 0 ${resolvedRadius}px` : `${resolvedRadius}px`,
					transition: fillTransition,
				}}
			/>
			{/* Scale marks hang BELOW the track so they stay legible whatever the fill/track colors
			    are. Only INNER marks get a hairline — at 0 and max the track edge already is the
			    mark, and a hairline on the rounded cap reads as a rendering artefact. */}
			{hasScale &&
				scaleMarks.map((mark, index) => {
					const markRatio = clamp(mark.value / max, 0, 1);
					if (markRatio <= 0 || markRatio >= 1) return null;
					return (
						<Box
							// biome-ignore lint/suspicious/noArrayIndexKey: callers pass coincident marks (a projection landing on the target), so value + label repeat; the marks are stateless, so a positional key re-renders identically.
							key={`scale-tick-${index}`}
							sx={{
								position: "absolute",
								top: "100%",
								height: SCALE_TICK_LENGTH_PX,
								left: `${markRatio * 100}%`,
								// Explicit px: MUI's sizing transform reads a bare `1` as `100%`, which
								// rendered the hairline as a full-width slab under the track.
								width: `${SCALE_TICK_WIDTH_PX}px`,
								bgcolor: mark.color ?? resolvedScaleTickColor,
								transform: "translateX(-50%)",
							}}
						/>
					);
				})}
			{/* Target tick — a bold vertical rule at `targetRatio`, straddling the track edges. */}
			{resolvedTargetRatio !== null && (
				<Box
					data-testid={dataTestId ? `${dataTestId}-target` : undefined}
					sx={{
						position: "absolute",
						top: -TARGET_TICK_OVERHANG_PX,
						bottom: -TARGET_TICK_OVERHANG_PX,
						left: `${resolvedTargetRatio * 100}%`,
						width: TARGET_TICK_WIDTH_PX,
						bgcolor: resolvedTargetColor,
						borderRadius: `${TARGET_TICK_WIDTH_PX / 2}px`,
						transform: "translateX(-50%)",
					}}
				/>
			)}
		</Box>
	);

	if (!hasScale) return meter;

	// Labels are positioned by their resolved left edge (px, from the measured track) rather than
	// centered on their value, so a label near either end pins flush inside the track instead of
	// overflowing it — and two near-coincident marks can't stack on top of each other.
	return (
		<Box sx={{ width: "100%" }}>
			{meter}
			<Box sx={{ position: "relative", height: FONT_SIZE.LABEL_XXS, mt: `${SCALE_TICK_LENGTH_PX + SCALE_LABEL_GAP_PX}px` }} aria-hidden>
				{placedLabels.map(({ mark, index, leftPx }) => (
					<Typography
						key={`scale-label-${index}`}
						component="span"
						sx={{
							position: "absolute",
							top: 0,
							left: `${leftPx}px`,
							fontSize: FONT_SIZE.LABEL_XXS,
							lineHeight: 1,
							fontVariantNumeric: "tabular-nums",
							whiteSpace: "nowrap",
							color: mark.color ?? resolvedScaleLabelColor,
						}}
					>
						{mark.label}
					</Typography>
				))}
			</Box>
		</Box>
	);
};
